import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HelpCircle, Target, Database, Zap, Shield, Search, Save, Trash2, Bot } from 'lucide-react';

const CTIGuideModal = ({ isOpen, onClose, className = '' }) => {
  if (!isOpen) return null;

  const steps = [
    {
      title: "Sélectionner un Chemin d'Attaque",
      description: "Choisissez le chemin d'attaque que vous souhaitez analyser. Vous pouvez cocher 'AI Components' si le chemin implique des composants d'intelligence artificielle.",
      icon: <Target size={20} className="text-blue-500" />
    },
    {
      title: "Choisir les Biens Supports",
      description: "Sélectionnez les biens supports (assets) à analyser. Ces biens seront scannés pour identifier les vulnérabilités et techniques d'attaque.",
      icon: <Database size={20} className="text-green-500" />
    },
    {
      title: "Lancer l'Analyse CTI",
      description: "Choisissez le type d'analyse : NIST (vulnérabilités), EUVD (vulnérabilités européennes), MITRE (techniques d'attaque), ou ATLAS (menaces IA).",
      icon: <Search size={20} className="text-orange-500" />
    },
    {
      title: "Consulter les Résultats",
      description: "Examinez les vulnérabilités et techniques trouvées dans les onglets séparés. Utilisez les filtres pour affiner votre analyse.",
      icon: <Zap size={20} className="text-purple-500" />
    },
    {
      title: "Gérer les Résultats",
      description: "Sauvegardez vos résultats ou supprimez l'analyse si nécessaire. Les données sont persistées par chemin d'attaque.",
      icon: <Save size={20} className="text-indigo-500" />
    }
  ];

  const analysisTypes = [
    {
      name: "NIST NVD",
      description: "Base de données nationale américaine des vulnérabilités",
      color: "bg-blue-100 text-blue-700",
      icon: <Shield size={16} />
    },
    {
      name: "EUVD",
      description: "Base de données européenne des vulnérabilités (ENISA)",
      color: "bg-indigo-100 text-indigo-700",
      icon: <Shield size={16} />
    },
    {
      name: "MITRE ATT&CK",
      description: "Framework des techniques d'attaque et tactiques",
      color: "bg-red-100 text-red-700",
      icon: <Target size={16} />
    },
    {
      name: "MITRE ATLAS",
      description: "Framework spécialisé pour les menaces IA/ML",
      color: "bg-purple-100 text-purple-700",
      icon: <Bot size={16} />
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className={`bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6 ${className}`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 flex justify-between items-center">
          <div className="flex items-center text-white">
            <HelpCircle size={24} className="mr-3" />
            <h2 className="text-xl font-bold">Guide Threat Intelligence</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            aria-label="Fermer le guide"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Process Steps */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Processus d'Analyse CTI</h3>
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800 mb-1">
                      {index + 1}. {step.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Analysis Types */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Types d'Analyse Disponibles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analysisTypes.map((type, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-2 ${type.color}`}>
                      {type.icon}
                      <span className="ml-1">{type.name}</span>
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{type.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <HelpCircle className="h-5 w-5 text-blue-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Conseils d'Utilisation</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Activez "AI Components" uniquement pour les chemins impliquant des systèmes IA</li>
                    <li>Utilisez les onglets pour séparer vulnérabilités et techniques d'attaque</li>
                    <li>Les filtres de sévérité vous aident à prioriser les vulnérabilités critiques</li>
                    <li>Sauvegardez régulièrement vos analyses pour éviter la perte de données</li>
                    <li>Les liens directs vous permettent d'accéder aux bases de données officielles</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CTIGuideModal;
